// src/features/manual/components/ManualRail.tsx

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';
import { Box, Button, Collapse, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ExpandMoreRounded as ExpandIcon, LockOutlined as StaffIcon } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useManualSearch } from '../hooks/useManual';
import type { ManualIndex, ManualLang, ManualSearchResult, ManualSection } from '../types';
import { manualSectionTarget, partTitleKey, railSections, sectionDomId } from '../utils/labels';
import { isSearchable } from '../utils/search';
import { ManualSearchField, ManualSearchResults } from './ManualSearch';

interface ManualRailProps {
  index: ManualIndex;
  activeChapterId: string | undefined;
  /** Sections of the open chapter, listed under it. */
  sections: ManualSection[];
  onJump: (anchor: string) => void;
  /** The manual's language: the search follows it. */
  lang: ManualLang;
}

/**
 * Table of contents: parts → chapters → the open chapter's sections, under the manual search. Sticky
 * beside the chapter on a wide screen; on a narrower one the search sits above a collapsible list.
 * While the search field has text, its results take the place of the list. A staff-only chapter
 * carries a lock (only staff ever receive it).
 */
export const ManualRail: React.FC<ManualRailProps> = ({ index, activeChapterId, sections, onJump, lang }) => {
  const translate = useTranslate();
  const theme = useTheme();
  const navigate = useNavigate();
  const wide = useMediaQuery(theme.breakpoints.up('lg'));
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const search = useManualSearch(query, lang);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Enter pressed before the results of that query arrived: open the first one when they do.
  const openFirstFor = useRef<string | null>(null);
  const searching = query.trim().length > 0;

  // A result opens the chapter at its section; on a phone the list gives way to the chapter.
  const onOpen = useCallback(
    (_result: ManualSearchResult) => {
      setOpen(false);
      if (!wide) setQuery('');
    },
    [wide]
  );

  const openFirst = useCallback(
    (results: ManualSearchResult[]) => {
      const first = results[0];
      if (!first) return;
      const { to, state } = manualSectionTarget(first.chapterId, first.anchor);
      navigate(to, { state });
      onOpen(first);
    },
    [navigate, onOpen]
  );

  const onSubmit = () => {
    const trimmed = query.trim();
    if (!isSearchable(trimmed)) return;
    if (search.data && search.query === trimmed && !search.isLoading) openFirst(search.data.results);
    else openFirstFor.current = trimmed;
  };

  useEffect(() => {
    if (openFirstFor.current === null || !search.data || search.isLoading) return;
    if (search.query === openFirstFor.current) {
      openFirstFor.current = null;
      openFirst(search.data.results);
    }
  }, [search.data, search.isLoading, search.query, openFirst]);

  const onQueryChange = (value: string) => {
    openFirstFor.current = null;
    setQuery(value);
  };

  const field = <ManualSearchField value={query} onChange={onQueryChange} onSubmit={onSubmit} inputRef={inputRef} />;
  const results = <ManualSearchResults query={query} search={search} onOpen={onOpen} />;
  const entries = railSections(sections);
  const item = theme.custom.menu.item;

  const linkSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    px: 1.25,
    py: 0.625,
    borderRadius: item.borderRadius,
    color: 'text.secondary',
    textDecoration: 'none',
    fontSize: '0.875rem',
    lineHeight: 1.4,
    '&:hover': { bgcolor: item.hover.backgroundColor(theme), color: item.hover.color(theme) },
    '&:focus-visible': { outline: 'none', boxShadow: theme.custom.focusRing },
  } as const;

  const list = (
    <Box component="nav" aria-label={translate('manual.rail.label')}>
      {index.parts
        .filter((part) => part.chapters.length > 0)
        .map((part) => (
          <Box key={part.part} sx={{ mb: 1 }}>
            <Typography
              component="h2"
              sx={{
                m: '12px 0 4px',
                px: 1.25,
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: theme.custom.text.tertiary,
              }}
            >
              {translate(partTitleKey(part.part))}
            </Typography>
            <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {part.chapters.map((chapter) => {
                const here = chapter.id === activeChapterId;
                return (
                  <Box component="li" key={chapter.id}>
                    <Box
                      component={RouterLink}
                      to={`/manual/${encodeURIComponent(chapter.id)}`}
                      aria-current={here ? 'page' : undefined}
                      onClick={() => setOpen(false)}
                      sx={{
                        ...linkSx,
                        ...(here && {
                          color: item.active.color(theme),
                          bgcolor: item.active.backgroundColor(theme),
                          fontWeight: item.active.fontWeight,
                          '&:hover': { bgcolor: item.active.hover.backgroundColor(theme) },
                        }),
                      }}
                    >
                      <Box component="span" sx={{ minWidth: 0, flex: 1 }}>
                        {chapter.title}
                      </Box>
                      {chapter.audience === 'staff' && (
                        <Tooltip title={translate('manual.staff.chapter')}>
                          <StaffIcon aria-label={translate('manual.staff.chapter')} sx={{ fontSize: 15, color: theme.custom.text.tertiary }} />
                        </Tooltip>
                      )}
                    </Box>
                    {here && entries.length > 0 && (
                      <Box component="ol" aria-label={translate('manual.rail.inChapter')} sx={{ listStyle: 'none', m: 0, p: 0, pl: 1.75 }}>
                        {entries.map((section) => (
                          <Box component="li" key={section.anchor}>
                            <Box
                              component="a"
                              href={`#${sectionDomId(section.anchor)}`}
                              onClick={(event: React.MouseEvent) => {
                                event.preventDefault();
                                setOpen(false);
                                onJump(section.anchor);
                              }}
                              sx={{ ...linkSx, fontSize: '0.8125rem' }}
                            >
                              {section.heading}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
    </Box>
  );

  if (wide) {
    return (
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          alignSelf: 'start',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100dvh - 160px)',
        }}
      >
        <Box sx={{ flexShrink: 0, pr: 1, pb: 0.5 }}>{field}</Box>
        <Box sx={{ minHeight: 0, overflowY: 'auto', pr: 1 }}>{searching ? results : list}</Box>
      </Box>
    );
  }

  const panelSx = {
    mt: 1,
    px: 1,
    pb: 1,
    bgcolor: 'background.paper',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.custom.radius.card,
  } as const;

  const toc = (
    <>
      <Button
        variant="outlined"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        endIcon={<ExpandIcon sx={{ transform: open ? 'rotate(180deg)' : 'none' }} />}
        fullWidth
        sx={{ justifyContent: 'space-between', minHeight: 44 }}
      >
        {translate('manual.rail.toggle')}
      </Button>
      <Collapse in={open} unmountOnExit>
        <Box sx={panelSx}>{list}</Box>
      </Collapse>
    </>
  );

  // One tree for both states, so the field keeps its focus when the first letter swaps the list out.
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ mb: 1 }}>{field}</Box>
      {searching ? <Box sx={{ ...panelSx, mt: 0 }}>{results}</Box> : toc}
    </Box>
  );
};

export default ManualRail;
